import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Tracks which slide indices should have their video `src` attached.
 * Once an index is activated it stays activated forever (no revocation).
 *
 * When a slide becomes visible, it activates itself and `index + 1`.
 */
export function useVisibilityActivation(totalItems: number) {
  const [activatedIndices, setActivatedIndices] = useState<Set<number>>(
    () => new Set<number>()
  );
  const observersRef = useRef<Map<number, IntersectionObserver>>(new Map());
  const slideRefCacheRef = useRef<Map<number, (node: HTMLElement | null) => void>>(new Map());

  useEffect(() => {
    return () => {
      observersRef.current.forEach((observer) => observer.disconnect());
      observersRef.current.clear();
      slideRefCacheRef.current.clear();
    };
  }, []);

  const activate = useCallback(
    (index: number) => {
      setActivatedIndices((prev) => {
        const next = new Set(prev);
        let changed = false;

        if (!next.has(index)) {
          next.add(index);
          changed = true;
        }

        const prefetch = index + 1;
        if (prefetch < totalItems && !next.has(prefetch)) {
          next.add(prefetch);
          changed = true;
        }

        return changed ? next : prev;
      });
    },
    [totalItems]
  );

  /**
   * Callback-ref factory. Attach to each slide's wrapper element:
   * ```
   * <div ref={slideRef(index)}>...</div>
   * ```
   */
  const slideRef = useCallback(
    (index: number) => {
      const cached = slideRefCacheRef.current.get(index);
      if (cached) return cached;

      const refCallback = (node: HTMLElement | null) => {
        // Clean up previous observer for this index
        const existing = observersRef.current.get(index);
        if (existing) {
          existing.disconnect();
          observersRef.current.delete(index);
        }

        if (!node) return;

        const observer = new IntersectionObserver(
          ([entry]) => {
            if (entry.isIntersecting) {
              activate(index);
              // Once activated, stop observing — activation is permanent
              observer.disconnect();
              observersRef.current.delete(index);
            }
          },
          { threshold: 0.3 }
        );

        observer.observe(node);
        observersRef.current.set(index, observer);
      };

      slideRefCacheRef.current.set(index, refCallback);
      return refCallback;
    },
    [activate]
  );

  return { activatedIndices, slideRef };
}
